import {
  Document,
  Paragraph,
  TextRun,
  Packer,
  AlignmentType,
  HeadingLevel,
  ImageRun
} from "docx";
import { saveAs } from "file-saver";

export const generateDOCXReport = async (
  filePairs,
  project,
  date,
  xResumoData = null,
  annotatedImagesUrls = [],
  customLogo = null,
  orientation = "portrait"
) => {
  const children = [
    // Cabeçalho clean
    new Paragraph({
      children: [
        new TextRun({
          text: "Vertex 4D",
          size: 28,
          bold: true,
          color: "333333",
          break: 1
        }),
        new TextRun({
          text: "Relatório de Inspeção Técnica",
          size: 20,
          bold: true,
          color: "333333"
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    }),

    // Informações do projeto
    new Paragraph({
      children: [
        new TextRun({
          text: `Projeto: ${project.name}`,
          size: 22,
          color: "555555"
        }),
        new TextRun({
          text: `\nTipo: ${project.type}`,
          size: 22,
          color: "555555"
        }),
        new TextRun({
          text: `\nData: ${formatDate(date)}`,
          size: 22,
          color: "555555"
        })
      ],
      spacing: { after: 600 }
    })
  ];

  // Conteúdo dos itens
  for (let i = 0; i < filePairs.length; i++) {
    const pair = filePairs[i];

    // Item título
    children.push(
      new Paragraph({
        text: `Item ${i + 1}: ${pair.title}`,
        heading: HeadingLevel.HEADING_2,
        spacing: { before: i > 0 ? 600 : 200, after: 200 }
      })
    );

    // Imagem (se existir)
    if (pair.imageUrl) {
      try {
        const imgResponse = await fetch(pair.imageUrl);
        const imgBlob = await imgResponse.blob();
        const imgArrayBuffer = await imgBlob.arrayBuffer();

        children.push(
          new Paragraph({
            children: [
              new ImageRun({
                data: imgArrayBuffer,
                transformation: {
                  width: 450,
                  height: 300
                }
              })
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 300 }
          })
        );
      } catch (error) {
        console.error("Erro ao carregar imagem:", error);
      }
    }

    // Descrição com processamento de markdown
    children.push(
      new Paragraph({
        text: "Descrição:",
        style: "DescriptiveText",
        spacing: { before: 200, after: 100 }
      })
    );

    // Processar descrição com markdown
    const descriptionParagraphs = processMarkdownText(pair.description);
    children.push(...descriptionParagraphs);

    // Ação recomendada com processamento de markdown
    children.push(
      new Paragraph({
        text: "Ação Recomendada:",
        style: "DescriptiveText",
        spacing: { before: 200, after: 100 }
      })
    );

    const actionParagraphs = processMarkdownText(pair.action);
    children.push(...actionParagraphs);

    // Container para severidade
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Severidade: ",
            bold: true
          }),
          new TextRun({
            text: pair.severity,
            color: getSeverityColor(pair.severity),
            bold: true
          })
        ],
        spacing: { before: 200, after: 400 },
        border: {
          top: {
            color: "EEEEEE",
            size: 4,
            style: "single"
          },
          bottom: {
            color: "EEEEEE",
            size: 4,
            style: "single"
          }
        },
        shading: {
          fill: "F9F9F9"
        }
      })
    );
  }

  // Adicionar seção de resumo executivo (resumo.json)
  const xResumoChildren = await addXResumoSection(project, date, xResumoData);
  children.push(...xResumoChildren);

  // Imagens anotadas, cada uma em uma página
  const annotatedChildren = await addAnnotatedImagesSection(annotatedImagesUrls);
  children.push(...annotatedChildren);

  // Rodapé clean
  children.push(
    new Paragraph({
      text: " ",
      spacing: { before: 600 }
    }),
    new Paragraph({
      text: `Gerado em: ${new Date().toLocaleDateString()}`,
      alignment: AlignmentType.RIGHT,
      style: "footer"
    }),
    new Paragraph({
      text: "Vertex4D - Soluções em Inspeção Técnica",
      alignment: AlignmentType.CENTER,
      style: "footer"
    })
  );

  const doc = new Document({
    styles: {
      paragraphStyles: [
        {
          id: "DescriptiveText",
          name: "Descriptive Text",
          basedOn: "Normal",
          run: {
            color: "444444",
            bold: true
          }
        },
        {
          id: "footer",
          name: "Footer",
          basedOn: "Normal",
          run: {
            size: 20,
            color: "888888"
          },
          paragraph: {
            spacing: {
              line: 276
            }
          }
        }
      ]
    },
    sections: [
      {
        properties: {
          page: {
            size: orientation === "landscape"
              ? { width: 16839, height: 11907, orientation: "landscape" }
              : { width: 11907, height: 16839 },
            margin: {
              top: 1000,
              right: 1000,
              bottom: 1000,
              left: 1000
            }
          }
        },
        children: children
      }
    ]
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `Relatorio_${project.name}_${formatDateForFilename(date)}.docx`);
};

// Função para processar texto com markdown
const processMarkdownText = text => {
  if (!text) return [];

  const paragraphs = [];
  const lines = text.split("\n");

  for (const line of lines) {
    if (!line.trim()) {
      // Linha vazia - adicionar espaçamento
      paragraphs.push(
        new Paragraph({
          text: "",
          spacing: { after: 120 }
        })
      );
      continue;
    }

    // Verificar se é um header markdown
    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);

    if (headerMatch) {
      const headerLevel = headerMatch[1].length;
      const headerText = headerMatch[2];

      // Aplicar formatação de header baseada no nível
      let headingLevel;
      switch (headerLevel) {
        case 1: // #
          headingLevel = HeadingLevel.HEADING_1;
          break;
        case 2: // ##
          headingLevel = HeadingLevel.HEADING_2;
          break;
        case 3: // ###
          headingLevel = HeadingLevel.HEADING_3;
          break;
        case 4: // ####
          headingLevel = HeadingLevel.HEADING_4;
          break;
        case 5: // #####
          headingLevel = HeadingLevel.HEADING_5;
          break;
        case 6: // ######
          headingLevel = HeadingLevel.HEADING_6;
          break;
        default:
          headingLevel = HeadingLevel.HEADING_3;
      }

      paragraphs.push(
        new Paragraph({
          text: headerText,
          heading: headingLevel,
          spacing: { before: 300, after: 200 }
        })
      );
    } else {
      // Processar linha normal com formatação markdown
      const formattedText = formatMarkdownText(line);
      if (formattedText.trim()) {
        paragraphs.push(
          new Paragraph({
            text: formattedText,
            spacing: { after: 120 }
          })
        );
      }
    }
  }

  return paragraphs;
};

// Função para formatar texto markdown (remover formatações)
const formatMarkdownText = text => {
  if (!text) return "";

  return (
    text
      // Remove negrito: **texto** ou __texto__
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/__(.*?)__/g, "$1")
      // Remove itálico: *texto* ou _texto_
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/_(.*?)_/g, "$1")
      // Remove código inline: `código`
      .replace(/`(.*?)`/g, "$1")
      // Remove links: [texto](url)
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      // Remove listas markdown (headers são processados separadamente)
      .replace(/^\s*[-*+]\s*/gm, "• ") // Converte listas para bullets
      .replace(/^\s*\d+\.\s*/gm, "") // Remove numeração de listas
      .trim()
  );
};

// Função para adicionar seção com dados do xresumo.json
const addXResumoSection = async (project, date, xResumoData = null) => {
  const children = [];

  try {
    // Se xResumoData não foi passado, tentar buscar
    if (!xResumoData) {
      // Construir URL do arquivo xresumo.json
      const folderPath = `projetos/${project.name}/${date}/relatorios`;
      const xResumoUrl = `${process.env.REACT_APP_API_URL ||
        "http://localhost:8000"}/files/${folderPath}/xresumo.json`;

      // Buscar dados do xresumo.json
      const response = await fetch(xResumoUrl);
      if (!response.ok) {
        return children;
      }

      xResumoData = await response.json();
    }

    // Quebra de página
    children.push(
      new Paragraph({
        text: "",
        pageBreakBefore: true
      })
    );

    // Título da seção
    children.push(
      new Paragraph({
        text: "RESUMO EXECUTIVO",
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        spacing: { before: 400, after: 400 }
      })
    );

    // Informações do projeto
    children.push(
      new Paragraph({
        text: "Informações do Projeto",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 300, after: 200 }
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: `Projeto: ${project.name}`,
            size: 22
          }),
          new TextRun({
            text: `\nTipo: ${project.type}`,
            size: 22
          }),
          new TextRun({
            text: `\nData: ${formatDate(date)}`,
            size: 22
          })
        ],
        spacing: { after: 300 }
      })
    );

    // Se resumo.json tem parecer completo (gerado pela IA), adicionar primeiro
    if (xResumoData && xResumoData.parecer && typeof xResumoData.parecer === "string") {
      children.push(
        new Paragraph({
          text: "Parecer da IA",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 200 }
        })
      );
      const parecerParagraphs = processMarkdownText(xResumoData.parecer);
      children.push(...parecerParagraphs);
    }

    // Extrair dados do JSON de forma mais inteligente
    let extractedData = xResumoData;

    // Se o JSON tem uma estrutura aninhada, extrair o conteúdo
    if (
      xResumoData &&
      xResumoData.resumo &&
      typeof xResumoData.resumo === "string"
    ) {
      try {
        // Remover markdown code blocks e processar a string
        let jsonString = xResumoData.resumo;

        // Remover ```json\n do início
        if (jsonString.startsWith("```json\n")) {
          jsonString = jsonString.substring(7);
        }

        // Remover ``` do final
        if (jsonString.endsWith("```")) {
          jsonString = jsonString.substring(0, jsonString.length - 3);
        }

        // Substituir \n por quebras de linha reais
        jsonString = jsonString.replace(/\\n/g, "\n");

        // Remover barras invertidas extras
        jsonString = jsonString.replace(/\\/g, "");

        // Tentar fazer parse do JSON dentro da string
        const parsedResumo = JSON.parse(jsonString);
        extractedData = parsedResumo;
      } catch (e) {
      }
    }

    // Se houver dados específicos, também exibi-los formatados
    if (extractedData.sumario_pontos) {;
      children.push(
        new Paragraph({
          text: "Sumário dos Pontos",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 200 }
        })
      );

      if (Array.isArray(extractedData.sumario_pontos)) {
        extractedData.sumario_pontos.forEach((ponto, index) => {
          children.push(
            new Paragraph({
              text: `${index + 1}. ${ponto}`,
              spacing: { after: 120 }
            })
          );
        });
      }
    }

    if (extractedData.oportunidades_melhoria) {;
      children.push(
        new Paragraph({
          text: "Oportunidades de Melhoria",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 200 }
        })
      );

      if (Array.isArray(extractedData.oportunidades_melhoria)) {
        extractedData.oportunidades_melhoria.forEach((oportunidade, index) => {
          children.push(
            new Paragraph({
              text: `${index + 1}. ${oportunidade}`,
              spacing: { after: 120 }
            })
          );
        });
      }
    }

    if (extractedData.referencias) {;
      children.push(
        new Paragraph({
          text: "Referências",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 200 }
        })
      );

      if (Array.isArray(extractedData.referencias)) {
        extractedData.referencias.forEach((referencia, index) => {
          children.push(
            new Paragraph({
              text: `${index + 1}. ${referencia}`,
              spacing: { after: 120 }
            })
          );
        });
      }
    }

    if (extractedData.conclusao_recomendacoes) {;
      children.push(
        new Paragraph({
          text: "Conclusão e Recomendações",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 200 }
        })
      );

      if (Array.isArray(extractedData.conclusao_recomendacoes)) {
        extractedData.conclusao_recomendacoes.forEach((conclusao, index) => {
          children.push(
            new Paragraph({
              text: `${index + 1}. ${conclusao}`,
              spacing: { after: 120 }
            })
          );
        });
      }
    }

    // Se nenhuma seção foi encontrada, exibir o JSON bruto para debug
    if (
      !extractedData.sumario_pontos &&
      !extractedData.oportunidades_melhoria &&
      !extractedData.referencias &&
      !extractedData.conclusao_recomendacoes
    ) {
      children.push(
        new Paragraph({
          text: "Dados Brutos do Resumo",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 200 }
        }),
        new Paragraph({
          text: JSON.stringify(extractedData, null, 2),
          spacing: { after: 300 }
        })
      );
    }
  } catch (error) {
    // Se houver erro, adiciona uma seção simples informando que o resumo não está disponível
    children.push(
      new Paragraph({
        text: "",
        pageBreakBefore: true
      }),
      new Paragraph({
        text: "Resumo Executivo",
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        spacing: { before: 400, after: 400 }
      }),
      new Paragraph({
        text: "Dados do resumo executivo não estão disponíveis.",
        alignment: AlignmentType.CENTER,
        spacing: { after: 300 }
      })
    );
  }

  return children;
};

// Adiciona imagens anotadas, cada uma em uma página
const addAnnotatedImagesSection = async (annotatedImagesUrls = []) => {
  const children = [];
  if (!annotatedImagesUrls || annotatedImagesUrls.length === 0) return children;
  for (let i = 0; i < annotatedImagesUrls.length; i++) {
    const url = annotatedImagesUrls[i];
    try {
      const imgResponse = await fetch(url);
      const imgBlob = await imgResponse.blob();
      const imgArrayBuffer = await imgBlob.arrayBuffer();
      children.push(
        new Paragraph({
          text: "",
          pageBreakBefore: true
        }),
        new Paragraph({
          text: `Imagem anotada ${i + 1}`,
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 200 }
        }),
        new Paragraph({
          children: [
            new ImageRun({
              data: imgArrayBuffer,
              transformation: {
                width: 450,
                height: 300
              }
            })
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 }
        })
      );
    } catch (err) {
      console.error("Erro ao carregar imagem anotada:", url, err);
    }
  }
  return children;
};

// Funções auxiliares
const formatDate = dateString => {
  const options = { day: "2-digit", month: "2-digit", year: "numeric" };
  return new Date(dateString).toLocaleDateString("pt-BR", options);
};

const formatDateForFilename = dateString => {
  const date = new Date(dateString);
  const day = date
    .getDate()
    .toString()
    .padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const getSeverityColor = severity => {
  switch (severity) {
    case "Alta":
      return "ff0000";
    case "Média":
      return "ff9900";
    case "Baixa":
      return "00aa00";
    default:
      return "000000";
  }
};
